/**
 * Riconfeziona un vocale WebM/Opus (quello che registra Chrome) in Ogg/Opus,
 * l'unico formato che Meta accetta come messaggio vocale sui numeri "Ufficiale"
 * ("Voice messages must be Ogg files encoded with the OPUS codec"; audio/webm
 * non e' supportato).
 *
 * Non ricodifica l'audio: prende i pacchetti Opus cosi' come sono dal contenitore
 * WebM (Matroska) e li mette in pagine Ogg (RFC 3533 + RFC 7845), con
 * intestazione OpusHead, OpusTags e posizione in campioni a 48 kHz.
 *
 * Supporta quello che produce MediaRecorder: un'unica traccia Opus, SimpleBlock
 * senza lacing, elementi a dimensione sconosciuta (registrazione in diretta).
 * In ogni altro caso restituisce null e il chiamante decide cosa fare.
 *
 * Modulo puro, testato in __tests__/webmOpusToOgg.test.ts.
 */

const ID = {
  EBML: 0x1a45dfa3,
  Segment: 0x18538067,
  Tracks: 0x1654ae6b,
  TrackEntry: 0xae,
  TrackNumber: 0xd7,
  CodecID: 0x86,
  CodecPrivate: 0x63a2,
  Audio: 0xe1,
  SamplingFrequency: 0xb5,
  Channels: 0x9f,
  Cluster: 0x1f43b675,
  BlockGroup: 0xa0,
  Block: 0xa1,
  SimpleBlock: 0xa3,
};

/** Elementi contenitore in cui entrare (gli altri si saltano). */
const MASTERS = new Set([ID.Segment, ID.Tracks, ID.TrackEntry, ID.Audio, ID.Cluster, ID.BlockGroup]);

interface Vint { value: number; length: number; unknown: boolean }

function readVint(buf: Uint8Array, pos: number, keepMarker: boolean): Vint | null {
  if (pos >= buf.length) return null;
  const first = buf[pos];
  let length = 1;
  let mask = 0x80;
  while (length <= 8 && !(first & mask)) { mask >>= 1; length++; }
  if (length > 8 || pos + length > buf.length) return null;
  let value = keepMarker ? first : first & (mask - 1);
  let allOnes = (first & (mask - 1)) === mask - 1;
  for (let i = 1; i < length; i++) {
    value = value * 256 + buf[pos + i];
    if (buf[pos + i] !== 0xff) allOnes = false;
  }
  return { value, length, unknown: !keepMarker && allOnes };
}

function readUint(buf: Uint8Array, start: number, size: number): number {
  let v = 0;
  for (let i = 0; i < size; i++) v = v * 256 + buf[start + i];
  return v;
}

function readFloat(buf: Uint8Array, start: number, size: number): number {
  const view = new DataView(buf.buffer, buf.byteOffset + start, size);
  return size === 4 ? view.getFloat32(0) : size === 8 ? view.getFloat64(0) : 0;
}

/** Campioni a 48 kHz contenuti in un pacchetto Opus (RFC 6716, sezione 3.1). */
export function opusPacketSamples(packet: Uint8Array): number {
  if (packet.length === 0) return 0;
  const toc = packet[0];
  const config = toc >> 3;
  let frame: number;
  if (config < 12) frame = [480, 960, 1920, 2880][config % 4];
  else if (config < 16) frame = config % 2 === 0 ? 480 : 960;
  else frame = [120, 240, 480, 960][config % 4];
  const code = toc & 0x03;
  const frames = code === 0 ? 1 : code === 3 ? (packet.length > 1 ? packet[1] & 0x3f : 0) : 2;
  return frame * frames;
}

// ─── Ogg ─────────────────────────────────────────────────────────────

let CRC_TABLE: Uint32Array | null = null;
function crcTable(): Uint32Array {
  if (CRC_TABLE) return CRC_TABLE;
  const t = new Uint32Array(256);
  for (let i = 0; i < 256; i++) {
    let r = i << 24;
    for (let j = 0; j < 8; j++) r = r & 0x80000000 ? (r << 1) ^ 0x04c11db7 : r << 1;
    t[i] = r >>> 0;
  }
  CRC_TABLE = t;
  return t;
}

/** CRC32 delle pagine Ogg (polinomio 0x04C11DB7, init 0, nessun xor finale). */
export function oggCrc(data: Uint8Array): number {
  const t = crcTable();
  let crc = 0;
  for (let i = 0; i < data.length; i++) crc = ((crc << 8) ^ t[((crc >>> 24) ^ data[i]) & 0xff]) >>> 0;
  return crc >>> 0;
}

function oggPage(packets: Uint8Array[], granule: number, serial: number, seq: number, flags: number): Uint8Array {
  const segments: number[] = [];
  for (const p of packets) {
    let n = p.length;
    while (n >= 255) { segments.push(255); n -= 255; }
    segments.push(n);
  }
  const bodyLen = packets.reduce((s, p) => s + p.length, 0);
  const page = new Uint8Array(27 + segments.length + bodyLen);
  const view = new DataView(page.buffer);
  page.set([0x4f, 0x67, 0x67, 0x53], 0); // "OggS"
  page[4] = 0;
  page[5] = flags;
  view.setUint32(6, granule % 0x100000000, true);
  view.setUint32(10, Math.floor(granule / 0x100000000), true);
  view.setUint32(14, serial, true);
  view.setUint32(18, seq, true);
  page[26] = segments.length;
  page.set(segments, 27);
  let off = 27 + segments.length;
  for (const p of packets) { page.set(p, off); off += p.length; }
  view.setUint32(22, oggCrc(page), true);
  return page;
}

function opusHead(channels: number, preSkip: number, sampleRate: number): Uint8Array {
  const h = new Uint8Array(19);
  const v = new DataView(h.buffer);
  h.set([0x4f, 0x70, 0x75, 0x73, 0x48, 0x65, 0x61, 0x64], 0); // "OpusHead"
  h[8] = 1;
  h[9] = channels;
  v.setUint16(10, preSkip, true);
  v.setUint32(12, sampleRate, true);
  v.setInt16(16, 0, true);
  h[18] = 0;
  return h;
}

function opusTags(): Uint8Array {
  const vendor = new TextEncoder().encode('Flusia');
  const t = new Uint8Array(8 + 4 + vendor.length + 4);
  const v = new DataView(t.buffer);
  t.set(new TextEncoder().encode('OpusTags'), 0);
  v.setUint32(8, vendor.length, true);
  t.set(vendor, 12);
  v.setUint32(12 + vendor.length, 0, true);
  return t;
}

const startsWith = (buf: Uint8Array, ascii: string) =>
  buf.length >= ascii.length && [...ascii].every((ch, i) => buf[i] === ch.charCodeAt(0));

export function isWebm(buf: Uint8Array): boolean {
  return buf.length >= 4 && readUint(buf, 0, 4) === ID.EBML;
}

export interface OggResult { bytes: Uint8Array; channels: number; durationSeconds: number }

export function webmOpusToOgg(input: Uint8Array): OggResult | null {
  if (!isWebm(input)) return null;

  let opusTrack: number | null = null;
  let codecPrivate: Uint8Array | null = null;
  let channels = 1;
  let sampleRate = 48000;
  let currentTrack: { number?: number; codec?: string; priv?: Uint8Array; channels?: number; rate?: number } | null = null;
  const packets: Uint8Array[] = [];

  const closeTrack = () => {
    if (currentTrack && currentTrack.codec === 'A_OPUS' && opusTrack === null && currentTrack.number !== undefined) {
      opusTrack = currentTrack.number;
      codecPrivate = currentTrack.priv ?? null;
      channels = currentTrack.channels ?? channels;
      sampleRate = currentTrack.rate ?? sampleRate;
    }
    currentTrack = null;
  };

  let pos = 0;
  while (pos < input.length) {
    const id = readVint(input, pos, true);
    if (!id) break;
    const size = readVint(input, pos + id.length, false);
    if (!size) break;
    const dataStart = pos + id.length + size.length;
    const dataEnd = size.unknown ? input.length : Math.min(input.length, dataStart + size.value);

    if (id.value === ID.TrackEntry) { closeTrack(); currentTrack = {}; }
    if (id.value === ID.Cluster) closeTrack();

    if (MASTERS.has(id.value)) { pos = dataStart; continue; }
    if (size.unknown) return null; // solo i contenitori possono avere dimensione sconosciuta

    const data = input.subarray(dataStart, dataEnd);
    switch (id.value) {
      case ID.TrackNumber: if (currentTrack) currentTrack.number = readUint(data, 0, data.length); break;
      case ID.CodecID: if (currentTrack) currentTrack.codec = new TextDecoder().decode(data).replace(/\0+$/, ''); break;
      case ID.CodecPrivate: if (currentTrack) currentTrack.priv = data.slice(); break;
      case ID.Channels: if (currentTrack) currentTrack.channels = readUint(data, 0, data.length); break;
      case ID.SamplingFrequency: if (currentTrack) currentTrack.rate = Math.round(readFloat(data, 0, data.length)); break;
      case ID.SimpleBlock:
      case ID.Block: {
        closeTrack();
        const track = readVint(data, 0, false);
        if (!track || opusTrack === null || track.value !== opusTrack) break;
        const flags = data[track.length + 2];
        if (flags & 0x06) return null; // lacing: MediaRecorder non lo usa
        const frame = data.slice(track.length + 3);
        if (frame.length > 0) packets.push(frame);
        break;
      }
    }
    pos = dataEnd;
  }
  closeTrack();
  if (opusTrack === null || packets.length === 0) return null;

  const cp = codecPrivate as Uint8Array | null;
  const head = cp && startsWith(cp, 'OpusHead') && cp.length >= 19 ? cp : opusHead(channels, 0, sampleRate);
  channels = head[9] || channels;

  const serial = (Date.now() & 0x7fffffff) >>> 0;
  const pages: Uint8Array[] = [oggPage([head], 0, serial, 0, 0x02), oggPage([opusTags()], 0, serial, 1, 0)];
  let seq = 2;
  let granule = 0;
  let batch: Uint8Array[] = [];
  let batchSegments = 0;
  let batchSamples = 0;
  const flush = (last: boolean) => {
    if (batch.length === 0) return;
    pages.push(oggPage(batch, granule, serial, seq++, last ? 0x04 : 0));
    batch = [];
    batchSegments = 0;
    batchSamples = 0;
  };
  for (let i = 0; i < packets.length; i++) {
    const p = packets[i];
    const segs = Math.floor(p.length / 255) + 1;
    if (segs > 255) return null;
    if (batchSegments + segs > 255 || batchSamples >= 48000) flush(false);
    batch.push(p);
    batchSegments += segs;
    const samples = opusPacketSamples(p);
    batchSamples += samples;
    granule += samples;
    if (i === packets.length - 1) flush(true);
  }

  const total = pages.reduce((s, p) => s + p.length, 0);
  const out = new Uint8Array(total);
  let off = 0;
  for (const p of pages) { out.set(p, off); off += p.length; }
  return { bytes: out, channels, durationSeconds: granule / 48000 };
}
